import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';

// All platforms with original Motion Blinds badge images
const platforms = [
  // Consumer platforms
  {
    id: 'apple-home',
    name: 'Apple Home',
    badge: '/img/badges/Works-with-badge_Small_Apple__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'google-home',
    name: 'Google Home',
    badge: '/img/badges/Works-with-badge_Small_Google__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'amazon-alexa',
    name: 'Amazon Alexa',
    badge: '/img/badges/Works-with-badge_Small_Alexa__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'smartthings',
    name: 'SmartThings',
    badge: '/img/badges/Works-with-badge_Small_Smartthings__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'home-assistant',
    name: 'Home Assistant',
    badge: '/img/badges/Works-with-badge_Small_Home-Assistent__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'ifttt',
    name: 'IFTTT',
    badge: '/img/badges/Works-with-badge_Small_IFTTT__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'homey-pro',
    name: 'Homey',
    badge: '/img/badges/Works-with-badge_Small_Homey__ScaleMaxWidthWzMwMF0.jpg'
  },
  // Professional systems
  {
    id: 'knx',
    name: 'KNX',
    badge: '/img/badges/Works-with-badges_Small_KNX__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'control4',
    name: 'Control4',
    badge: '/img/badges/Works-with-badge_Small_Control4__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'loxone',
    name: 'Loxone',
    badge: '/img/badges/Works-with-badge_Small_Loxone__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'abb',
    name: 'ABB',
    badge: '/img/badges/Works-with-badge_Small_ABB__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'busch-jaeger',
    name: 'Busch-Jaeger',
    badge: '/img/badges/Works-with-badge_Small_Busch-Jaeger__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'elan',
    name: 'ELAN',
    badge: '/img/badges/Works-with-badge_Small_Elan__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'bond',
    name: 'Bond',
    badge: '/img/badges/Works-with-badge_Small_Bond__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'mediola',
    name: 'Mediola',
    badge: '/img/badges/Works-with-badge_Small_Mediola__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'smart-home-hornbach',
    name: 'Smart Home by Hornbach',
    badge: '/img/badges/Works-with-badges_Small_Smart-Home__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'klikaanklikuit',
    name: 'KlikAanKlikUit',
    badge: '/img/badges/Works-with-badge_Small_Klikaan__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'rti',
    name: 'RTI',
    badge: '/img/badges/Works-with-badges_Small_RTI__ScaleMaxWidthWzMwMF0.jpg'
  },
  {
    id: 'matter',
    name: 'Matter',
    badge: '/img/badges/matter_lkup_rgb_night-1__ScaleMaxWidthWzMwMF0.webp'
  },
];

export const WorksWithPage = () => {
  return (
    <Layout>
      {/* Back Button */}
      <div className="container-xl pt-24 pb-2">
        <Link to="/motorization/smart-connectivity" className="inline-flex items-center text-neutral-500 hover:text-neutral-900 transition-colors">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Smart Connectivity
        </Link>
      </div>

      {/* Hero Section */}
      <section className="bg-neutral-100 pt-8 pb-16" data-testid="works-with-hero">
        <div className="container-xl">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-neutral-900 text-5xl lg:text-6xl font-serif leading-tight mb-4">
              Works with Motionblinds
            </h1>
            <p className="text-2xl lg:text-3xl text-neutral-500 font-light">
              All platforms. All devices.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative h-[40vh] overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&q=80"
          alt="Smart home living room"
          className="w-full h-full object-cover"
        />
      </section>

      {/* Platform Badges Grid */}
      <section className="section-padding" data-testid="platforms-grid">
        <div className="container-xl">
          <h2 className="text-3xl font-serif mb-10 text-center">Works with Motionblinds</h2>
          
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-4">
            {platforms.map((platform, index) => (
              <motion.div
                key={platform.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
              >
                <Link 
                  to={`/motorization/smart-connectivity/${platform.id}`}
                  className="block bg-white rounded-xl border border-neutral-200 overflow-hidden hover:shadow-lg hover:border-neutral-300 transition-all"
                >
                  <img 
                    src={platform.badge} 
                    alt={`Works with ${platform.name}`}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-neutral-50" data-testid="works-with-cta">
        <div className="container-xl">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold mb-2">Where to buy Motionblinds?</h3>
              <p className="text-neutral-600 mb-6">Find a retailer or dealer near you.</p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-neutral-900 hover:bg-neutral-800 text-white">
                    Find your store
                  </Button>
                </Link>
                <Link to="/trade">
                  <Button variant="outline">
                    For professionals
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-semibold mb-2">Support center | FAQ</h3>
              <p className="text-neutral-600 mb-6">Get answers to common questions.</p>
              <Link to="/faq">
                <Button className="bg-neutral-900 hover:bg-neutral-800 text-white">
                  Find your answer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default WorksWithPage;
